import React from "react";
import { useManageCollectionPageTabs } from "./utils/useManageCollectionPageTabs.js";
import { prepareCoverConfig } from "./utils/prepareCoverConfig.js";
import { prepareBodyConfig } from "./utils/prepareBodyConfig.js";
import { ReadOnlyLayout } from "./comps/ReadOnlyLayout/index.js";
import { EditLayout } from "./comps/EditLayout/index.js";
import { useCreateIndexList } from "@/utils/templateIndex/useCreateIndexList.js";
import { TableOfContent } from "@/components/generalUI/TableOfContent/index.js";
import { getBreakPoint, useScreenWidth } from "oolib";
import { usePlatformBuilderContext } from "@/contexts/platformBuilderContext.js";
import { useAutoGenCollectionSubSpaces } from "./utils/useAutoGenCollectionSubSpaces.js";
import { useAutoGenFilterConfigsFromTpl } from "@/components/analysisModules/utils/useAutoGenFilterConfigsFromTpl/index.js";
import { useGetQueryData } from "@/utils/react-query-hooks/general.js";

const Layout = ({
  tpl,
  content,
  readOnly: readOnlyProp,
  memo,
  handleOnChange,
  activeTab: activeTabFromParent,
  setActiveTab: setActiveTabInParent,
  showIndexbar,
  indexType,
  inTCI,
}) => {
  const screenWidth = useScreenWidth();
  const { indexList, activeIndexOp, setActiveIndexOp } = useCreateIndexList({
    tpl,
    content,
    readOnly: readOnlyProp,
  });
  const originalSubSpaces = tpl.kp_templates.body.subSpaces.filter(
    (ss) => ss.subSpaceHiddenInTCI !== true
  );

  const subSpaces = useAutoGenCollectionSubSpaces({ originalSubSpaces, content });

  const { SELF_MANAGED_BASE_CONFIGS, deployment } = useGetQueryData("platformConfigs");
  const allTpls = useGetQueryData("allTpls");
  const autoGenCollectionPagesEnabled = SELF_MANAGED_BASE_CONFIGS?.enable || deployment?._AutoGenCollectionPages?.enable;

  const { tabOps, activeTab, setActiveTab } = useManageCollectionPageTabs({
    subSpaces,
    readOnlyProp,
    content,
    activeTabFromParent,
    setActiveTabInParent,
  });

  // Following the same pattern as PublishedListing:
  // call useAutoGenFilterConfigsFromTpl with the active tab's tpl
  const activeSubSpace = subSpaces.find((ss) => ss.value === activeTab);
  const activeContentType =
    activeSubSpace?.builder === "ListingBuilder"
      ? activeSubSpace.configs?.contentTypes?.[0]
      : null;
  const activeTpl = activeContentType
    ? allTpls?.find((t) => t.kp_content_type === activeContentType)
    : null;

  const _autoGenFilterConfig = useAutoGenFilterConfigsFromTpl({
    selectedTpls: activeTpl ? [activeTpl] : [],
    filterScopes: ["doc"],
    enable: autoGenCollectionPagesEnabled,
    unifiedFilterGroup:
      SELF_MANAGED_BASE_CONFIGS?.listingConfig?.filterConfig?.unifiedFilterGroup ?? !autoGenCollectionPagesEnabled,
    flatDocFilters: true,
  });

  const coverConfig = prepareCoverConfig({
    tpl,
    content,
    configs: tpl.kp_templates.intro.configs,
  });

  const {inTemplateBuilder} = usePlatformBuilderContext();
  const enableListingStyleSwitcher = SELF_MANAGED_BASE_CONFIGS?.listingConfig?.enableListingStyleSwitcher ?? autoGenCollectionPagesEnabled;
  const bodyConfig = prepareBodyConfig({
    subSpaces, content, memo, inTemplateBuilder: inTemplateBuilder || inTCI,
    smFilterConfig: _autoGenFilterConfig,
    enableListingStyleSwitcher,
  });

  const genIndexBar = (props = {}) => {
    const { formValidation } = props;

    return (
      <TableOfContent
        type={"anchorLinks"}
        indexType={indexType}
        options={indexList}
        value={activeIndexOp}
        onChange={(k, v) => setActiveIndexOp(v)}
        failedBlocks={
          formValidation &&
          formValidation.makeVisible &&
          formValidation.failedBlocks
        }
      />
    );
  };

  return (
    <>
      {readOnlyProp ? (
        <ReadOnlyLayout
          content={content}
          coverConfig={coverConfig}
          bodyConfig={bodyConfig}
          tabOps={tabOps}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          collectionId={content._id}
          genIndexBar={showIndexbar ? genIndexBar : undefined}
          isDataPivotEnabled={SELF_MANAGED_BASE_CONFIGS?.listingConfig?.dataPivot?.enable ?? autoGenCollectionPagesEnabled}
          isOverviewDashboardEnabled={SELF_MANAGED_BASE_CONFIGS?.listingConfig?.overviewDashboard?.enable ?? autoGenCollectionPagesEnabled}
        />
      ) : (
        <EditLayout
          tpl={tpl}
          content={content}
          memo={memo}
          handleOnChange={handleOnChange}
          genIndexBar={showIndexbar ? genIndexBar : undefined}
        />
      )}
    </>
  );
};

export default Layout;